import { ELEVATION_LEVELS } from "../constants.js";
import type { Palette } from "../data/palettes.js";
import { idiv, shr } from "../engine/int.js";

/**
 * Colour slots.
 *
 * Sixteen slots, so a slot index fits in four bits — the low nibble of a cell byte.
 * The glyph index takes the high nibble; see `art/cell.ts` for why a cell is a whole
 * byte rather than the seven bits an earlier version of this comment claimed.
 *
 * The order is fixed forever: `Const.sol` mirrors these numbers.
 */
export const SLOT_BG = 0;
/** Elevation band `L` lives at `SLOT_TERRAIN + L`, for L in [0, ELEVATION_LEVELS). */
export const SLOT_TERRAIN = 1;
export const SLOT_PLUMAGE_DARK = 9;
export const SLOT_PLUMAGE_MID = 10;
export const SLOT_PLUMAGE_LIGHT = 11;
export const SLOT_BREAST = 12;
export const SLOT_ACCENT = 13;
export const SLOT_SPARK = 14;
export const SLOT_INK = 15;
export const SLOT_COUNT = 16;

/** Maximum tint pull, out of 256. Kept low so authored relationships survive. */
export const MAX_TINT = 48;

/**
 * Smallest tint pull a token may actually be given, out of 256.
 *
 * Below this the tint is a label on nothing — the same objection that gives
 * archetypes a one-cell separation floor. A token either wears its tint visibly or
 * draws `None`, which is a real trait value with its own weight and a strength of
 * exactly zero. There is no continuum between them.
 */
export const MIN_TINT = 16;

/**
 * The tint is a coloured light over the *scene*, not a repaint of the *subject*.
 *
 * Applying one strength to every slot was measurably wrong. A saturated pull on a
 * near-monochrome light palette lifts the dark plumage far more, in relative
 * luminance, than it lifts the mid-dark breast — so Salt's single red mark faded
 * into its own bird at full cyan (ratio 1.37, authored 2.19). Tinting the
 * atmosphere at full strength and the creature at half preserves the authored
 * subject relationships while keeping the mood shift clearly visible, and it is
 * also the physically sensible reading: ambient colour washes the environment,
 * the animal keeps its identity.
 *
 * Atmosphere: `bg`, all terrain bands. Subject: plumage, breast, accent, spark.
 * `ink` is never tinted at all.
 */
export function subjectTint(strength: number): number {
  return shr(strength, 1);
}


/** Extract one channel. Safe with `>>` because colours never exceed 0xFFFFFF. */
function chan(c: number, shift: number): number {
  return (c >> shift) & 0xff;
}

function pack(r: number, g: number, b: number): number {
  return r * 65536 + g * 256 + b;
}

/**
 * Per-channel integer interpolation, `t` in [0, 256]. Exact at both endpoints.
 * Uses `idiv` (truncating) rather than a shift because `(b - a)` is signed.
 */
export function lerpColor(a: number, b: number, t: number): number {
  const r = chan(a, 16) + idiv((chan(b, 16) - chan(a, 16)) * t, 256);
  const g = chan(a, 8) + idiv((chan(b, 8) - chan(a, 8)) * t, 256);
  const bl = chan(a, 0) + idiv((chan(b, 0) - chan(a, 0)) * t, 256);
  return pack(r, g, bl);
}

/**
 * Expand four authored terrain stops into `ELEVATION_LEVELS` bands.
 *
 * Position is computed in 1/256 units across three segments so the maths stays
 * integral and the endpoints land exactly on the outer stops.
 */
export function terrainRamp(stops: readonly [number, number, number, number]): number[] {
  const out: number[] = [];
  const last = ELEVATION_LEVELS - 1;
  for (let level = 0; level < ELEVATION_LEVELS; level++) {
    const pos = idiv(level * 3 * 256, last); // 0..768
    let seg = pos >> 8;
    if (seg > 2) seg = 2;
    const frac = pos - seg * 256;
    out.push(lerpColor(stops[seg] as number, stops[seg + 1] as number, frac));
  }
  return out;
}

/**
 * Resolve a palette plus a token's tint into the flat slot table the renderer
 * uses. `tintStrength` is in [0, MAX_TINT] and is the *atmosphere* strength; the
 * creature's own slots receive `subjectTint()` of it.
 *
 * `ink` is never tinted: outlines are the only thing separating the bird from the
 * ground, and pulling them toward a hue collapses that separation on the palettes
 * where ground and plumage are already close.
 */
export function resolveSlots(palette: Palette, tintRgb: number, tintStrength: number): number[] {
  const air = tintStrength < 0 ? 0 : tintStrength > MAX_TINT ? MAX_TINT : tintStrength;
  const sub = subjectTint(air);
  const tintAir = (c: number): number => (air === 0 ? c : lerpColor(c, tintRgb, air));
  const tintSub = (c: number): number => (sub === 0 ? c : lerpColor(c, tintRgb, sub));

  const slots = new Array<number>(SLOT_COUNT).fill(0);
  slots[SLOT_BG] = tintAir(palette.bg);

  const ramp = terrainRamp(palette.terrain);
  for (let level = 0; level < ELEVATION_LEVELS; level++) {
    slots[SLOT_TERRAIN + level] = tintAir(ramp[level] as number);
  }

  slots[SLOT_PLUMAGE_DARK] = tintSub(palette.plumage[0]);
  slots[SLOT_PLUMAGE_MID] = tintSub(palette.plumage[1]);
  slots[SLOT_PLUMAGE_LIGHT] = tintSub(palette.plumage[2]);
  slots[SLOT_BREAST] = tintSub(palette.breast);
  slots[SLOT_ACCENT] = tintSub(palette.accent);
  slots[SLOT_SPARK] = tintSub(palette.spark);
  slots[SLOT_INK] = palette.ink;
  return slots;
}

/** Lowercase six-digit hex, no `#`. */
export function hex6(c: number): string {
  return c.toString(16).padStart(6, "0");
}
